import { useEffect, useRef, useState } from 'react'
import { useLang } from '../i18n'
import { AppearanceSection } from './AppearanceSection'
import { AiAgentSection } from './AiAgentSection'
import { SettingsSearch } from './SettingsSearch'
import { GraphDiagnosticsSection, useDiagnosticsAccess } from './GraphDiagnosticsSection'
import { KbSourcesSection } from './KbSourcesSection'
import { UserActivitySection } from './UserActivitySection'
import { useBidProfile } from '../lib/bid-profile'
import type { SettingsSection as Section } from '../lib/router'

// Render/nav order: AI agent first, Appearance second. Three more are appended CONDITIONALLY
// after them — 'kb-sources' then 'user-activity' for a tenant admin, then 'diagnostics' for the
// one account useDiagnosticsAccess() allows. All three stay out of the nav entirely for everyone
// else, so nobody is offered a section whose every request would 403.
const SECTION_ORDER: Section[] = ['ai-agent', 'appearance']

/**
 * The global (non-bid) account settings page — reached from the top-right account menu's
 * "AI settings" entry (TopBar.tsx), or deep-linked straight to a section via
 * lib/router.ts#aiAgentSettingsPath. Every section is stacked in `.settings-main` and ALWAYS
 * MOUNTED — the left nav is a table of contents, not a tab switcher. That is also why
 * KbSourcesSection polls only while an indexation run is actually in progress: an always-mounted
 * query on a 5s interval would keep hitting the backend for as long as the page is open.
 * Clicking a nav item scrolls to its section; scrolling the page updates which item is current in
 * return (IntersectionObserver, feature-detected — jsdom has no such observer, and clicking a nav
 * item sets the current one directly, so navigation itself never depends on the live highlight
 * working). Only `.settings-main` scrolls (see styles.css) — the nav is a plain fixed sidebar that
 * never moves, not a shared scroll region.
 *
 * `.settings-main`'s height is pinned with an explicit inline pixel value (paneHeight, measured
 * off `.settings-body` by ResizeObserver), not left to CSS align-items:stretch, which could not be
 * trusted to hold once padding entered the picture (see extraBottomPadding below).
 *
 * That padding is itself sized off the SAME measurement, capped comfortably under paneHeight —
 * never a flat `vh` guess. The reason is a `box-sizing:border-box` rule easy to forget: a box
 * cannot render shorter than its own top+bottom padding (the content area can't go negative), so
 * ANY explicit height smaller than that padding sum is silently overridden to the padding sum
 * instead — confirmed empirically: computed height kept coming back as exactly
 * paddingTop+paddingBottom, never the requested height, for as long as padding-bottom was left at
 * a generic `100vh`. Keeping padding-bottom strictly under paneHeight avoids that trap entirely
 * while still reserving enough room for a short, LAST section (Appearance) to scroll flush to the
 * top of the pane.
 */
export function AccountSettingsPage({ initialSection }: { initialSection?: Section }) {
  const { t } = useLang()
  const hasDiagnosticsAccess = useDiagnosticsAccess()
  // Gated on isTenantAdmin DIRECTLY, never on isResolved: this page has no bid, and `isResolved`
  // is false whenever the bid is null, so gating on it would hide the section from everyone. The
  // client check is an affordance — the backend enforces the same rule independently, and refuses
  // every catalogue endpoint before it queries anything.
  const { isTenantAdmin } = useBidProfile(null)
  const sections: Section[] = [
    ...SECTION_ORDER,
    ...(isTenantAdmin ? (['kb-sources', 'user-activity'] as const) : []),
    ...(hasDiagnosticsAccess ? (['diagnostics'] as const) : []),
  ]
  const [current, setCurrent] = useState<Section>(initialSection ?? 'ai-agent')
  const bodyRef = useRef<HTMLDivElement>(null)
  const mainRef = useRef<HTMLDivElement>(null)
  const sectionEls = useRef<Partial<Record<Section, HTMLElement>>>({})
  const [paneHeight, setPaneHeight] = useState<number | null>(null)
  // Comfortably under paneHeight (see the component doc comment) — the 56px margin is a safety
  // buffer against rounding, not a value that matters precisely.
  const extraBottomPadding = paneHeight != null ? Math.max(0, paneHeight - 56) : null

  // Measure .settings-body's own (CSS-determined, stable) height and pin nav+main to EXACTLY
  // that — see the component doc comment above for why this bypasses CSS stretch entirely.
  useEffect(() => {
    const el = bodyRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height
      if (h) setPaneHeight(h)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  function sectionLabel(s: Section): string {
    if (s === 'appearance') return t('settings_section_appearance')
    if (s === 'kb-sources') return t('kbsrc_section_title')
    if (s === 'user-activity') return t('ua_section_title')
    if (s === 'diagnostics') return 'Graph diagnostics' // temporary, single-account — not localized
    return t('settings_section_ai_agent')
  }

  function scrollToSection(s: Section, behavior: ScrollBehavior): void {
    sectionEls.current[s]?.scrollIntoView({ behavior, block: 'start' })
  }

  // Deep-link entry (e.g. the chat panel's "Go to settings" CTA) — jump straight there, no
  // animation, the same way a browser jumps to a URL fragment. Runs once, on mount, only.
  useEffect(() => {
    if (initialSection) scrollToSection(initialSection, 'auto')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Scrollspy: keep the nav in sync with whichever section is nearest the top of the pane.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const root = mainRef.current
    if (!root) return
    const observer = new IntersectionObserver(
      (entries) => {
        const topMost = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        const id = topMost?.target.getAttribute('data-section') as Section | undefined
        if (id) setCurrent(id)
      },
      // Only the top 30% of the pane counts as "current" — otherwise a short section scrolled
      // fully into view would fight its neighbour just below it for the title.
      { root, rootMargin: '0px 0px -70% 0px' },
    )
    for (const el of Object.values(sectionEls.current)) if (el) observer.observe(el)
    return () => observer.disconnect()
  }, [])

  function onNavClick(s: Section): void {
    setCurrent(s)
    scrollToSection(s, 'smooth')
  }

  return (
    <main className="settings" aria-label={t('account_settings_title')}>
      <div className="settings-head">
        <h1>{t('account_settings_title')}</h1>
        <SettingsSearch containerRef={mainRef} />
        {/* Reuses the same close affordance as every modal in the app (modal-close + t('close')),
            even though this is a full page, not a modal — it plays the same role: leave this
            surface and go back to wherever the member was (browser history, not a fixed route,
            since this page is reachable from any view via the account menu). */}
        <button className="modal-close" aria-label={t('close')} title={t('close')} onClick={() => window.history.back()}>✕</button>
      </div>
      <div className="settings-body" ref={bodyRef}>
        <nav
          className="settings-nav"
          aria-label={t('account_settings_sections')}
          style={paneHeight != null ? { height: `${paneHeight}px` } : undefined}
        >
          <ul className="settings-navlist">
            {sections.map((s) => (
              <li key={s}>
                <button
                  className={`settings-navitem${current === s ? ' is-current' : ''}`}
                  aria-current={current === s ? 'page' : undefined}
                  onClick={() => onNavClick(s)}
                >
                  {sectionLabel(s)}
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <div
          className="settings-main"
          ref={mainRef}
          style={
            paneHeight != null
              ? { height: `${paneHeight}px`, paddingBottom: `${extraBottomPadding}px` }
              : undefined
          }
        >
          {sections.map((s) => (
            <section
              key={s}
              ref={(el) => {
                sectionEls.current[s] = el ?? undefined
              }}
              data-section={s}
              className="settings-section"
              aria-labelledby={`settings-h-${s}`}
            >
              <h2 id={`settings-h-${s}`} className="settings-main-title">{sectionLabel(s)}</h2>
              {s === 'appearance' ? (
                <AppearanceSection />
              ) : s === 'kb-sources' ? (
                <KbSourcesSection />
              ) : s === 'user-activity' ? (
                <UserActivitySection />
              ) : s === 'diagnostics' ? (
                <GraphDiagnosticsSection />
              ) : (
                <AiAgentSection />
              )}
            </section>
          ))}
        </div>
      </div>
    </main>
  )
}
